import { useEffect, useState } from "react";
import { Button, Form, Spinner } from 'react-bootstrap';

import { Validators } from "../../../shared/utils/Validators";
import { TextInput, SelectInput } from "../../../shared/form/FormInput";
import { plansList, userList } from "../../../shared/settings/apiConfig";
import { getApi } from "../../../shared/services/apiHelper";
import { assignPlan, changePlan } from "../../../shared/services/adminApi";

import '../clients/ClientAddEdit.scss';
import { PlusIcon } from "../../../shared/icons/Icons";

export default function PlanAssign(props) {

    const [planOptions, setPlanOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [formLoading, setFormLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [assignForm, setAssignForm] = useState({
        user_id: { value: '', valid: false, error: '', validators: [Validators.required()] },
        plan_id: { value: '', valid: false, error: '', validators: [Validators.required()] },
        count: { value: '1', valid: true, error: '', validators: [] },
    });
    const [formValid, setFormValid] = useState(false);

    useEffect(() => {
        loadDropdownData();
        
        // Pre-select user from URL if provided
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('user');
        if (userId) {
            setAssignForm(prevState => ({
                ...prevState,
                user_id: { ...prevState.user_id, value: userId, valid: true }
            }));
        }
    }, []);

    useEffect(() => {
        setFormValid(Validators.validateForm(assignForm));
    }, [assignForm]);

    const loadDropdownData = () => {
        setFormLoading(true);
        Promise.all([
            getApi(plansList).catch(() => null),
            getApi(userList).catch(() => null)
        ]).then(([plansRes, usersRes]) => {
            if (plansRes?.data?.data) {
                const opts = plansRes.data.data
                    .filter(p => p.is_active !== false)
                    .map(p => ({ 
                        value: p._id || p.id,              // MongoDB ID (stored in form)
                        option: `${p.name} (${p.code})`    // Display text (shown in dropdown)
                    }));
                setPlanOptions(opts);
            }
            if (usersRes?.data) {
                // Handle both array and paginated responses
                const users = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data || []);
                const opts = users.map(u => ({ 
                    value: u._id || u.id,                         // MongoDB ID (stored in form)
                    option: u.email || u.username || u.name       // Display text (shown in dropdown)
                }));
                setUserOptions(opts);
            }
            setFormLoading(false);
        }).catch(() => {
            setFormLoading(false);
            setErrorMessage('Failed to load dropdown data');
        });
    };

    const inputChange = (type, valueOrEvent) => {
        let value = valueOrEvent;
        if (valueOrEvent?.target) {
            value = valueOrEvent.target.value;
        }
        setAssignForm(prevState => {
            return {
                ...prevState,
                [type]: Validators.validateFormControl(prevState[type], value)
            }
        });
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const onAssignPlan = () => {
        setFormLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        const data = {
            user_id: assignForm.user_id.value,
            plan_id: assignForm.plan_id.value,
            count: parseInt(assignForm.count.value) || 1,
        };

        assignPlan(data).then((response) => {
            setSuccessMessage('Plan assigned successfully!');
            setFormLoading(false);
            setTimeout(() => {
                props.history.goBack();
            }, 1500);
        }).catch((error) => {
            setFormLoading(false);
            setErrorMessage(error.response?.data?.detail || "Error assigning plan");
            setTimeout(() => {
                setErrorMessage('');
            }, 3000);
        });
    };

    const onChangePlan = () => {
        setFormLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        const data = {
            user_id: assignForm.user_id.value,
            plan_id: assignForm.plan_id.value,
            count: parseInt(assignForm.count.value) || 1,
        };

        changePlan(data).then((response) => {
            setSuccessMessage('Plan changed successfully!');
            setFormLoading(false);
            setTimeout(() => {
                props.history.goBack();
            }, 1500);
        }).catch((error) => {
            setFormLoading(false);
            setErrorMessage(error.response?.data?.detail || "Error changing plan");
            setTimeout(() => {
                setErrorMessage('');
            }, 3000);
        });
    };

    return (
        <div className="main-content client-box w-100">
            <div className="d-flex flex-column gap-2">
                <h4 className="d-flex fw-bold gap-3">Assign / Change Plan</h4>
                <p className='content-sub-label'>Assign a subscription plan to a user or change their existing plan</p>
            </div>

            <Form className="d-flex flex-column justify-content-center my-3 gap-3">
                <div className="d-flex gap-3">
                    <SelectInput name={'user_id'} label={'Select User'} placeholder={'Choose a user...'}
                        value={assignForm.user_id.value} error={assignForm.user_id.error} isMandatory={true}
                        options={userOptions} onChange={(event) => inputChange("user_id", event)} />

                    <SelectInput name={'plan_id'} label={'Select Plan'} placeholder={'Choose a plan...'}
                        value={assignForm.plan_id.value} error={assignForm.plan_id.error} isMandatory={true}
                        options={planOptions} onChange={(event) => inputChange("plan_id", event)} />

                    <TextInput type={'number'} name={'count'} label={'Count'} placeholder={'1'}
                        value={assignForm.count.value} error={assignForm.count.error}
                        onChange={(event) => inputChange("count", event)} />
                </div>
            </Form>

            {errorMessage && (
                <div className="d-flex align-items-center justify-content-center text-danger">{errorMessage}</div>
            )}
            {successMessage && (
                <div className="d-flex align-items-center justify-content-center text-success">{successMessage}</div>
            )}

            <div className="d-flex align-items-center justify-content-center mt-5" style={{ gap: '10px' }}>
                <Button className="btn-primary mt-4 rounded px-4 w-auto" onClick={onCancel}>
                    Cancel
                </Button>
                <Button className="primary-gradient-btn mt-4 rounded px-4 w-auto" onClick={onAssignPlan} disabled={!formValid || formLoading}>
                    <PlusIcon />Assign Plan {formLoading ? <Spinner className='ms-2' as="span" animation="border" role="status" aria-hidden="true" /> : ''}
                </Button>
                <Button className="secondary-btn-gradient mt-4 rounded px-4 w-auto" onClick={onChangePlan} disabled={!formValid || formLoading}>
                    Change Plan {formLoading ? <Spinner className='ms-2' as="span" animation="border" role="status" aria-hidden="true" /> : ''}
                </Button>
            </div>
        </div>
    );
}